import React from 'react';
import { useTranslate } from 'react-admin';

import Alert from '@material-ui/lab/Alert';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import LinearProgress from '@material-ui/core/LinearProgress';

import { HeadTransaction } from '~/types/HeadTransaction';
import { TransactionStatus } from '~/types/TransactionStatus';

import { l } from './RunTransferAction';

type RunTransferActionResultProps = {
  open: boolean;
  inProgress: boolean;
  transaction?: HeadTransaction;
  onClose: () => void;
  onGoToTransaction: (transactionId: string) => void;
};

const severity = (status: TransactionStatus) => {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'REJECTED':
      return 'error';
    default:
      return 'info';
  }
};

const RunTransferActionResult = ({
  open,
  inProgress,
  transaction,
  onClose,
  onGoToTransaction,
}: RunTransferActionResultProps) => {
  const translate = useTranslate();
  const t = (key: string, options?: any): string => translate(l(key), options);

  const statusMessage = (status: TransactionStatus): string => {
    switch (status) {
      case 'COMPLETED':
        return t('result.completed');
      case 'AWAITING_APPROVAL':
        return t('result.awaitingApproval');
      case 'REJECTED':
        return t('result.rejected');
      default:
        return t('result.other', {
          status: translate(`components.ra.fields.StatusField.${status}`, { _: status }),
        });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby='run-transfer-result-dialog-title'
      fullWidth
      maxWidth='xs'
    >
      <DialogTitle id='run-transfer-result-dialog-title'>{t('result.title')}</DialogTitle>
      <DialogContent>
        {inProgress ? (
          <Box mt={1} mb={3}>
            <LinearProgress />
          </Box>
        ) : transaction ? (
          <Alert severity={severity(transaction.status)}>{statusMessage(transaction.status)}</Alert>
        ) : null}
      </DialogContent>
      {!inProgress && (
        <DialogActions>
          <Button onClick={onClose}>{t('result.closeDialog')}</Button>
          {transaction && (
            <Button color='primary' onClick={() => onGoToTransaction(transaction.id)}>
              {t('result.goToTransaction')}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default RunTransferActionResult;
